import React, {useEffect, useState} from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { paths } from 'routes';
import { useLocale } from 'i18n/useLocale';
import { DELEGON_URL } from 'configuration';

import { ContentLayout } from 'website/components/Layout/components/ContentLayout';

import { useBlogBySlug } from '../api/getBlogBySlug';
import { useTrackVisit } from 'hook/useTrackVisit';
import { isoToDateString } from 'utils/dateFormatter';
import { BlogPicture } from '../components/BlogPicture';
import { Loader } from 'components/Loader';
import { ImageAvatar } from 'components/Avatar';
// import { ReactComponent as LinkedinLogo } from 'assets/linkedin logo.svg';
import DelegonSmallLogo from 'assets/Delegon_Small_Logo.png';
import { Subscribe } from 'website/components/SubscribeSection';
import { LinkedinIcon, LinkedinShareButton } from 'react-share';
import { RecentBlogPosts } from 'website/components/RecentBlogPosts';
import { CompanyProfile } from 'components/CompanyProfile/CompanyProfile';
import BackgroundImage from '../assets/background.png';

export const DetailsView = () => {  
  let { slug } = useParams();
  const lang = useLocale();
  const { t } = useTranslation();
  const [ loading, setLoading ] = useState(false);
  const [timer, setTimer] = useState(null);
  const [ meta, setMeta ] = useState([]);

  const { data: blog, isLoading, isFetched, isError, error } = useBlogBySlug(slug);
  useTrackVisit('blog', blog?.blog_id, 'website');

  if(loading && !isLoading) {
    setLoading(false);
  }

  useEffect(() => {  
    if (isLoading) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isFetched) {
    if (loading) {      
      setTimeout(() => setLoading(false), 1000);
    }
  }

  useEffect(() => {
    if (blog) {
      setMeta(
        [
          { name: 'description', content: blog?.description },
          { property: 'og:title', content: `Delegon | ${blog?.title}` },
          { property: 'og:description', content: blog?.description },
          { property: 'og:image', content: blog?.picture_url },
          { property: 'og:url', content: `${DELEGON_URL}${paths.public.blogDetailsView(blog?.slug, lang)}` },
          { property: 'og:type', content: 'article' },
          { property: 'og:publish_date', content: blog?.created_on },
        ]
      );
    }
  }, [blog, lang])
  
  
  if (!loading && isError) {
    const err = JSON.parse(error?.response?.data?.error)
    return <div>{err.error}</div>;
  }

  if (isLoading || loading) {
    return <Loader style={{marginTop: "40vh"}}/>;
  }

  return (
    <ContentLayout meta={meta} title={blog?.description || "Delegon"} hreflang={false}>
      {isLoading && !loading ? (
        <></>
      )
      : (
        loading ? (
          <Loader style={{marginTop: "40vh"}}/>
        )
        : (
          <>
          <Stack height={1} width={"100vw"} mx={2} maxWidth={{xs: 600, md: 800}} justifySelf={"center"} alignSelf={"center"} mb={5}>
            <Stack sx={{px: 5, gap: 5, display: "flex"}}>
              {loading && (
                <Loader style={{marginTop: "40vh"}} />
              )}

              {!loading && (
                <>
                <Stack position="relative" flexDirection="column" gap={4} sx={{mt: 5, mb: 2}}>
                  <Stack gap={1}>
                    <Chip
                      label={blog?.tag || ''}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#fff",
                        backgroundColor: "primary.light",
                        borderRadius: "6px",
                        width: "fit-content",
                      }}
                    />
                    <Typography variant="h3" sx={{fontSize: {xs: "1.5625rem", md: "2.5rem"}, fontWeight: 600, lineHeight: 1.45, letterSpacing: 0, wordWrap: 'break-word' }}>
                      {blog?.title || ''}
                    </Typography>

                    <Stack flexDirection={"row"} sx={{alignItems: "center", justifyContent: "space-between"}} gap={1} width={1}>
                      <Stack flexDirection={"row"} sx={{alignItems: "center"}} gap={1}>
                        <ImageAvatar
                          img={(blog?.Creator && blog?.Creator?.company_id !== 0) ? blog?.Creator?.square_logo_url : DelegonSmallLogo || ''}
                          name={(blog?.Creator && blog?.Creator?.company_id !== 0) ? blog?.Creator?.Name : "Delegon"}
                          sx={{
                            backgroundColor: "#fff",
                            width: {xs: 20, md: 25},
                            height: {xs: 20, md: 25},
                            img : {
                              width: {xs: 20, md: 25},
                              height: {xs: 20, md: 25},
                              overflow: 'hidden',
                            },
                          }}
                        />
                        <Stack flexDirection={"column"}>
                          <Typography variant="body3" sx={{fontFeatureSettings: `'liga' off, 'vlig' off`, fontSize: "0.625rem", fontWeight: 400, lineHeight: "normal"}}>
                            {(blog?.Creator && blog?.Creator?.company_id !== 0) ? blog?.Creator?.Name : "Delegon"}
                          </Typography>
                          <Typography variant="body3" sx={{fontSize: "0.625rem", fontWeight: 400, lineHeight: "normal"}}>
                            {isoToDateString(blog?.created_on, false)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Box sx={{height: 24, width: 24, display: "flex", alignItems: "center", justifyContent: "center", pt: 0.5, boxSizing: "border-box"}}>
                        <LinkedinShareButton url={`${DELEGON_URL}${paths.public.blogDetailsView(blog?.slug, lang)}`}>
                          <LinkedinIcon size={24} borderRadius={16} />
                        </LinkedinShareButton>
                      </Box>
                    </Stack>
                  </Stack>

                  <BlogPicture picture={blog?.picture_url} />

                  <Typography variant='body2' sx={{fontSize: "1rem", fontWeight: 400, lineHeight: 1.75, whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>
                    {blog?.content}
                  </Typography>
                </Stack>
                <CompanyProfile company_name={blog?.Creator?.Name} square_logo_url={blog?.Creator?.square_logo_url} banner_logo_url={blog?.Creator?.background_image_url} description={blog?.Creator?.CompanyProfile?.about_us} slug={blog?.Creator?.CompanyProfile?.slug} company_id={blog?.Creator?.company_id} />
                <RecentBlogPosts label={t('website:blogDetails.recentPostsLabel')} count={3} currentPostID={blog?.blog_id} px={0} />
                <Stack
                  sx={{
                    backgroundImage: `url(${BackgroundImage})`,
                    backgroundColor: 'grey.300',
                    backgroundPosition: 'center',
                    backgroundSize: "cover",
                    backgroundRepeat: 'no-repeat',
                    position: "relative",
                    // The parent Stack centers children that are wider
                    // than it (align-items: center), which throws off the
                    // left:50% / margin-left:-50vw full-bleed trick on
                    // mobile. Stretching this box to the parent's start
                    // edge keeps that math correct.
                    alignSelf: "flex-start",
                    ml: "-50vw",
                    left: "50%",
                    width: "100vw",
                  }}
                >
                  <Stack
                    width={1}
                    maxWidth={{xs:664, md: 1200}}
                    mx="auto"
                    boxSizing="border-box"
                    px={{xs: 4, md: 6}}
                    sx={{
                      // Subscribe's own top/bottom padding is uneven
                      // (30px/50px); shift it down by half the difference
                      // so it's visually centered in the picture without
                      // changing the total height.
                      mt: 1.25,
                      mb: -1.25,
                    }}
                  >
                    <Subscribe />
                  </Stack>
                </Stack>
              </>
              )}
            </Stack>
          </Stack>
          {/* <Stack justifySelf={"center"} alignSelf={"center"} sx={{mb: 2}}>
            <Subscribe />
          </Stack> */}
          </>
        )
      )}
    </ContentLayout>
  );
};
